/**
 * Access Control Utility
 * Handles role detection and domain-based permissions
 */

import { loadLocal, STORAGE_KEYS } from './storage.js'

/**
 * Determine the default role based on the hostname
 * @returns {string} 'admin' or 'public'
 */
export function getDomainRole() {
    const host = window.location.hostname

    // Developer override
    const urlParams = new URLSearchParams(window.location.search)
    const roleOverride = urlParams.get('role') || localStorage.getItem('manatiming_role_override')
    if (roleOverride) return roleOverride

    // Staff domains (includes local network IPs for testing)
    if (
        host === 'timing.manarunning.com' ||
        host.includes('localhost') ||
        host === '127.0.0.1' ||
        host.startsWith('192.168.') ||  // Local network
        host.startsWith('10.') ||        // Local network
        host.startsWith('172.')          // Local network
    ) {
        return 'admin'
    }

    // Default to public
    return 'public'
}

/**
 * Check if the user is on the staff timing domain
 * @returns {boolean}
 */
export function isStaffDomain() {
    return getDomainRole() === 'admin'
}

/**
 * Get the current active role (combines domain and session passcode)
 * @returns {string} 'admin', 'scorer', or 'public'
 */
export function getCurrentRole() {
    const domainRole = getDomainRole()

    // If we are on a staff domain, allow staff roles
    if (domainRole === 'admin') {
        const passcode = localStorage.getItem('manatiming_passcode')
        if (passcode === 'admin') return 'admin'
        if (passcode === 'scorer') return 'scorer'

        // On staff domain but no passcode yet? Still return 'admin' by default 
        // unless we want to force login. For now, let's stick to the user's
        // simplified requirement where timing subdomain is for staff.
        return 'admin'
    }

    // On public domain, always return public regardless of passcode (simplified security)
    return 'public'
}

/**
 * Check if the user has a specific permission
 * @param {string} permission - Permission name
 * @returns {boolean}
 */
export function hasPermission(permission) {
    const role = getCurrentRole()

    const permissions = {
        admin: ['view_results', 'view_live', 'edit_results', 'manage_chute', 'manage_timers', 'manage_races', 'manage_roster'],
        scorer: ['view_results', 'view_live', 'edit_results', 'manage_chute'],
        public: ['view_results', 'view_live']
    }

    return (permissions[role] || []).includes(permission)
}
